import { useState, type FormEvent } from 'react'
import { ChangeEmailError, changeEmailFlow } from '@features/account/services/changeEmailFlow'
import { Notice } from '@shared/ui/Notice'
import { PasswordInput } from '@shared/ui/PasswordInput'
import { Icon } from '@/icons/Icon'
import { useTranslation } from '@/i18n'

/**
 * Settings § account: メールアドレス変更フォーム (T-H3)。
 *
 * - 新 email + 現マスターパスワード入力
 * - submit でクライアント側パスワード検証 + SRP material 再導出 + POST /users/me/email/change-init
 * - 成功時は「確認メールを送信しました」表示 + ボタンを再活性化しない (重複送信抑止)
 *   ユーザーが新 email でリンクをクリックして確定する。
 * - SRP / 暗号鍵の派生はすべて service 側 (`changeEmailFlow`)。本コンポーネントは状態管理のみ。
 */

type Phase =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success'; pendingEmail: string }
  | { kind: 'error'; messageKey: string }

const ERROR_KEY: Record<ChangeEmailError['code'], string> = {
  no_session: 'settings.account.change_email.error.no_session',
  invalid_email: 'settings.account.change_email.error.invalid_email',
  same_email: 'settings.account.change_email.error.same_email',
  wrong_password: 'settings.account.change_email.error.wrong_password',
  email_taken: 'settings.account.change_email.error.email_taken',
  too_many_requests: 'settings.account.change_email.error.too_many_requests',
  network: 'settings.account.change_email.error.network',
  generic: 'settings.account.change_email.error.generic',
}

export interface ChangeEmailSectionProps {
  onClose?: () => void
}

export const ChangeEmailSection = ({ onClose }: ChangeEmailSectionProps): JSX.Element => {
  const { t } = useTranslation()
  const [newEmail, setNewEmail] = useState('')
  const [password, setPassword] = useState('')
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })

  const onSubmit = async (ev: FormEvent<HTMLFormElement>): Promise<void> => {
    ev.preventDefault()
    if (phase.kind === 'submitting' || phase.kind === 'success') return
    setPhase({ kind: 'submitting' })
    try {
      const res = await changeEmailFlow({ newEmail, masterPassword: password })

      setPassword('')
      setPhase({ kind: 'success', pendingEmail: res.pendingEmail })
    } catch (err: unknown) {
      const code: ChangeEmailError['code'] = err instanceof ChangeEmailError ? err.code : 'generic'

      setPhase({ kind: 'error', messageKey: ERROR_KEY[code] })
    }
  }

  const isSubmitting = phase.kind === 'submitting'

  return (
    <div
      className="vault-delete-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="change-email-title"
      onClick={ev => {
        if (ev.target === ev.currentTarget && !isSubmitting) onClose?.()
      }}
    >
      <div className="vault-delete-dialog change-email-dialog">
        {phase.kind === 'success' ? (
          <div data-testid="change-email-success">
            <h3 id="change-email-title">{t('settings.account.change_email.title')}</h3>
            <Notice tone="info" ico="check">
              {t('settings.account.change_email.success', { email: phase.pendingEmail })}
            </Notice>
            <div className="vault-delete-actions">
              <button type="button" className="btn btn-primary" onClick={onClose}>
                {t('settings.account.change_email.close')}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={ev => void onSubmit(ev)} data-testid="change-email-form">
            <h3 id="change-email-title">{t('settings.account.change_email.title')}</h3>
            <Notice tone="warn" ico="alert">
              {t('settings.account.change_email.warning')}
            </Notice>

            <div className="field">
              <label className="field-label" htmlFor="change-email-new">
                {t('settings.account.change_email.new_email')}
              </label>
              <input
                id="change-email-new"
                type="email"
                className="input"
                value={newEmail}
                onChange={ev => setNewEmail(ev.target.value)}
                required
                autoComplete="email"
                data-testid="change-email-new-input"
              />
            </div>

            <div className="field">
              <label className="field-label" htmlFor="change-email-password">
                {t('settings.account.change_email.master_password')}
              </label>
              <PasswordInput
                id="change-email-password"
                name="masterPassword"
                autoComplete="current-password"
                value={password}
                onChange={ev => setPassword(ev.target.value)}
                required
              />
            </div>

            {phase.kind === 'error' && (
              <Notice tone="danger" ico="alert">
                {t(phase.messageKey)}
              </Notice>
            )}

            <div className="vault-delete-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={onClose}
                disabled={isSubmitting}
              >
                {t('settings.account.change_email.cancel')}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
                data-testid="change-email-submit"
              >
                <Icon name="mail" aria-hidden="true" />
                {isSubmitting
                  ? t('settings.account.change_email.submitting')
                  : t('settings.account.change_email.submit')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
